'use client';

import { useEffect, useRef } from 'react';
import type { RefObject } from 'react';

// Custom hook for infinite scrolling with Intersection Observer and throttle
const useInfiniteScroll = (
  isEnabled: boolean,
  callback: (() => void) | null = null,
  rootElementKey: string = '',
  rootMargin: string = '0px',
  threshold: number = 0.1,
): RefObject<HTMLDivElement | null> => {
  const targetRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const options: IntersectionObserverInit = {
      // use the viewport as the root
      root: rootElementKey ? document.getElementById(rootElementKey) : null,
      rootMargin,
      threshold, // threshold for triggering the callback
    };

    const observerCallback: IntersectionObserverCallback = (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && callback) {
          callback(); // Call the provided callback when bottom is reached
        }
      });
    };

    // Create the Intersection Observer instance
    const observer = isEnabled ? new IntersectionObserver(observerCallback, options) : null;
    const refElement = targetRef.current;
    if (refElement) {
      observer?.observe(refElement);
    }

    return () => {
      if (refElement) {
        observer?.unobserve(refElement);
      }
    };
  }, [callback, isEnabled, rootElementKey, rootMargin, threshold]);

  return targetRef;
};

export default useInfiniteScroll;
